/* eslint-disable no-console */
import {
  formatUsage,
  PLANNER_DEBUG,
  type PlannerTurnLog,
  type PreReadLog,
  preview,
} from './format';

const TAG = '%c planner ';
const BADGE = 'background:#4b2ea8;color:#fff;border-radius:3px;font-weight:600';
const PLAIN = 'background:transparent;color:inherit;font-weight:400';

/**
 * Browser-console log of the client side of a turn: tool calls handed to the browser,
 * client-action batches, what the couple approved, and each action's result. Ends with
 * the turn's token use. Silent in production.
 */
export const logModelToolCall = (name: string, input: unknown): void => {
  if (!PLANNER_DEBUG) return;
  console.log(`${TAG}%c tool call → ${name}  ${preview(input)}`, BADGE, PLAIN);
};

/**
 * Result of the `__aiPlannerClearThread()` console helper. `readable: false` means the
 * store did not answer, which is not the same as an empty thread. Each record that was
 * not deleted gets its own warning, since leftover state skews the next run.
 */
export const logPlannerThreadCleared = ({
  cleared,
  readable,
  loop,
  memory,
  wedding,
}: {
  cleared: number;
  readable: boolean;
  loop?: boolean;
  memory?: boolean;
  wedding?: boolean;
}): void => {
  if (!readable) {
    console.warn(`${TAG}%c thread NOT cleared — the store could not be read`, BADGE, PLAIN);
    return;
  }
  const also = [
    loop === true ? 'loop' : null,
    memory === true ? 'memory' : null,
    wedding === true ? 'wedding' : null,
  ].filter(Boolean);
  console.log(
    `${TAG}%c thread cleared (${cleared} chunk${cleared === 1 ? '' : 's'})${
      also.length > 0 ? ` + ${also.join(' + ')}` : ''
    } → chat reset`,
    BADGE,
    PLAIN,
  );
  for (const [name, result] of [
    ['loop', loop],
    ['memory', memory],
    ['wedding', wedding],
  ] as const) {
    if (result === false) {
      console.warn(`${TAG}%c ${name} NOT cleared — the next run starts part-used`, BADGE, PLAIN);
    }
  }
};

/**
 * The moment the conversation leaves onboarding: what was settled and what was asked, so
 * the reader knows which rule fired. Logged in every environment, because the app layout
 * changes at this point.
 */
export const logOnboardingExit = ({
  settled,
  asked,
  at,
}: {
  settled: readonly string[];
  asked: readonly string[];
  at: number;
}): void => {
  console.log(
    `${TAG}%c onboarding exit → app leaves focused mode  settled=[${settled.join(', ') || '-'}]  asked=[${asked.join(', ') || '-'}]  at=${new Date(at).toISOString()}`,
    BADGE,
    PLAIN,
  );
};

// A data part that arrives outside the message, such as the kick-off review.
export const logDataPart = (type: string, data: unknown): void => {
  if (!PLANNER_DEBUG) return;
  console.log(`${TAG}%c ${type}  ${preview(data, 200)}`, BADGE, PLAIN);
};

// How one client action resolved: its access rules and the executor that runs it.
export type ClientActionRow = {
  action: string;
  access: string;
  permission: string;
  executor: string;
  lazyEpics: string;
  payload: string;
};

// The batch as the card sees it: one row per action with its access rules and executor,
// so a wrong mapping shows before anything runs.
export const logClientActionBatch = ({
  toolCallId,
  rows,
  needsConsent,
  denied,
  refusals,
  settled = [],
}: {
  toolCallId: string;
  rows: ClientActionRow[];
  needsConsent: boolean;
  denied: string[];
  refusals: string[];
  settled?: string[];
}): void => {
  if (!PLANNER_DEBUG) return;
  console.groupCollapsed(
    `${TAG}%c batch ${toolCallId.slice(0, 8)}  ${rows.length} action(s)  ${
      needsConsent ? 'awaiting consent' : 'running'
    }`,
    BADGE,
    PLAIN,
  );
  console.table(rows);
  if (denied.length > 0) {
    console.log(`declined by the couple's permissions: ${denied.join(', ')}`);
  }
  if (refusals.length > 0) {
    console.log(`refused up front: ${refusals.join(', ')}`);
  }
  if (settled.length > 0) {
    console.log(`already in force, settled without running: ${settled.join(', ')}`);
  }
  console.groupEnd();
};

export const logConsentDecision = (toolCallId: string, approved: boolean): void => {
  if (!PLANNER_DEBUG) return;
  console.log(
    `${TAG}%c batch ${toolCallId.slice(0, 8)}  ${approved ? 'approved' : 'declined'} by the couple`,
    BADGE,
    PLAIN,
  );
};

// The Redux hand-off: which action creator runs, and whether epics were injected.
export const logClientActionDispatch = ({
  type,
  kind,
  epics,
  epicsInjected,
  payload,
}: {
  type: string;
  kind: string;
  epics?: string;
  epicsInjected?: boolean;
  payload: unknown;
}): void => {
  if (!PLANNER_DEBUG) return;
  const epicNote = epics ? `  epics=${epics}${epicsInjected ? ' (injected now)' : ' (ready)'}` : '';
  console.log(`${TAG}%c ${type} → ${kind}${epicNote}  ${preview(payload)}`, BADGE, PLAIN);
};

// What one client action did, in full rather than shortened for a console line.
export type ClientActionOutcome = {
  type: string;
  ok: boolean;
  ms: number;
  error?: string;
  data?: unknown;
  at: number;
};

// Enough for any one turn's batch, small enough to stay bounded in a long conversation.
const CLIENT_ACTION_HISTORY = 50;
const clientActionLog: ClientActionOutcome[] = [];

/**
 * What the client actions of recent turns actually did. Their results stay in the
 * browser, so this is the only way to check what the receipt card reports.
 *
 * Bound to `__aiPlannerClientActions()` in the browser.
 */
export const readClientActionLog = (): ClientActionOutcome[] => [...clientActionLog];

export const logClientActionResult = ({
  type,
  ok,
  ms,
  error,
  data,
}: {
  type: string;
  ok: boolean;
  ms: number;
  error?: string;
  data?: unknown;
}): void => {
  if (!PLANNER_DEBUG) return;
  clientActionLog.push({ type, ok, ms, error, data, at: Date.now() });
  if (clientActionLog.length > CLIENT_ACTION_HISTORY) clientActionLog.shift();
  const outcome = ok ? `dispatched in ${ms}ms` : `failed: ${error ?? 'unknown'}`;
  console.log(
    `${TAG}%c ${ok ? '✓' : '✗'} ${type}  ${outcome}${
      data !== undefined ? `  data=${preview(data, 200)}` : ''
    }`,
    BADGE,
    PLAIN,
  );
};

// The pre-read in short: the failure, or the ask state, the fact and the confidence.
const preReadSummary = (preRead: PreReadLog): string =>
  preRead.read
    ? `${preRead.read.askState} ${preRead.read.askedFact ?? '-'} ${preRead.read.confidence.toFixed(2)}${
        preRead.read.gated ? '' : ' below gate'
      } ${preRead.ms}ms`
    : `${preRead.failure} ${preRead.ms}ms`;

export const logTurnLog = (log: PlannerTurnLog): void => {
  if (!PLANNER_DEBUG) return;
  console.groupCollapsed(
    `${TAG}%c turn done  ${log.model}  locked=${log.locked}  steps=${log.steps}  ${formatUsage(
      log.usage,
    )}${log.extract ? `  +extract ${log.extract.ms}ms ${formatUsage(log.extract.usage)}` : ''}${
      log.timing
        ? `  first text ${log.timing.firstTextMs ?? '-'}ms  last text ${log.timing.lastTextMs ?? '-'}ms  model end ${log.timing.modelEndMs ?? '-'}ms  extract wait ${log.timing.extractWaitMs ?? '-'}ms  finish ${log.timing.finishMs}ms`
        : ''
    }${log.preRead ? `  preread ${preReadSummary(log.preRead)}` : ''}`,
    BADGE,
    PLAIN,
  );
  if (log.toolCalls.length > 0) {
    console.table(
      log.toolCalls.map((call) => ({
        tool: call.name,
        ms: call.ms,
        ok: call.ok,
        resolvedBy: call.clientResolved ? 'client' : 'server',
      })),
    );
  }
  if (log.providerTail?.entries.length) {
    const tail = log.providerTail;
    console.log(
      `provider after last text: ${tail.entries
        .map((entry) => `${entry.type} ×${entry.count} ${entry.fromMs}–${entry.toMs}ms`)
        .join(', ')}${tail.dropped ? ` (+${tail.dropped} more)` : ''}`,
    );
  }
  console.log(`finish reason: ${log.finishReason}`);
  console.groupEnd();
};
